import { useCallback, useMemo, useState } from 'react';

import { Box, Button, useTheme } from '@mui/material';
import { PlusIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import { type BuyerLeadDnDColumnFragment } from '@graphql/__generated__/graphql';
import { BuyerLeadColumnTitle } from '@modules/_shared/components/buyer-lead/BuyerLeadColumnTitle';
import { BuyerLeadDeleteDialog } from '@modules/_shared/components/buyer-lead/BuyerLeadDeleteDialog';
import { BuyerLeadKanbanItem } from '@modules/_shared/components/buyer-lead/BuyerLeadKanbanItem';
import { KanbanColumn } from '@modules/_shared/components/kanban/KanbanColumn';
import { getStageCardColors } from '@modules/_shared/utils/leadStageSelectHelpers';
import { useLocale } from '@shared/hooks/locale';

export const BUYER_LEAD_DND_COLUMN_FRAGMENT = gql(/* GraphQL */ `
  fragment BuyerLeadDnDColumn on buyer_leads {
    id
    stage_id
    order_nr
    lead_stage {
      id
      status
    }
    source
    created_at
    updated_at
    next_activity: activities(
      where: {
        done: { _eq: false }
        activity_type: { _in: [call, visit, task, assignment] }
      }
      order_by: { consolidated_due_at: asc_nulls_last }
      limit: 1
    ) {
      ...BuyerLeadDashboardActivity
    }
    user {
      ...UserCard_user
    }
    lot {
      id
      offer_type
      sale_price
      lot_reference
      broker_id
      broker {
        id
        active_teams_users: teams_users(
          where: { deactivated: { _eq: false } }
        ) {
          team_id
          team {
            id
            lots_member_access
          }
        }
      }
    }
    # Cross-tenant MLS-safe fallbacks. user/lot above resolve through
    # the owner-tenant RLS chain and return null for MLS buyer_leads
    # whose lot/user aren't reachable to the agent that way. mls_user
    # (users_view, narrow column allowlist) and mls_lot (mls_lots view)
    # surface display-safe data via the proper cross-tenant boundary.
    mls_user {
      id
      first_name
      last_name
      full_name
      primary_email
      primary_phone_number
      profile_image_url
    }
    mls_lot {
      id
      offer_type
      sale_price
      lot_reference
      broker_id
    }
  }
`);

interface BuyerLeadDnDColumnProps {
  index: number;
  column: {
    data: { label?: string | null; status?: string | null; id: string };
    id: string;
  };
  onNewBtnClicked: (targetStageId: string) => void;
  onItemClicked?: (item: BuyerLeadDnDColumnFragment) => void;
  items: BuyerLeadDnDColumnFragment[];
  includeLotReference?: boolean;
  readOnly?: boolean;
  canEditLead?: (lead: BuyerLeadDnDColumnFragment) => boolean;
}

export const BuyerLeadDnDColumn: React.FC<BuyerLeadDnDColumnProps> = ({
  index,
  column,
  onNewBtnClicked,
  onItemClicked,
  items,
  includeLotReference,
  readOnly = false,
  canEditLead,
}) => {
  const { t } = useLocale();
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);
  const { id: columnId, data: columnData } = column;
  const columnTitle = useMemo(
    () => (
      <BuyerLeadColumnTitle
        label={columnData.label}
        status={columnData.status}
        count={items.length}
      />
    ),
    [columnData.label, columnData.status, items],
  );

  const endControls = useMemo(
    () =>
      readOnly ? null : (
        <Box
          sx={{
            p: 1,
          }}
        >
          <Button
            onClick={() => onNewBtnClicked(columnId as string)}
            size="small"
            startIcon={<PlusIcon />}
            sx={{ mx: 'auto' }}
          >
            {t('Add buyer lead')}
          </Button>
        </Box>
      ),
    [onNewBtnClicked, columnId, t, readOnly],
  );

  const editableById = useMemo(
    () =>
      new Map(items.map(item => [item.id, canEditLead?.(item) ?? !readOnly])),
    [items, canEditLead, readOnly],
  );

  const renderItem = useCallback(
    (data: BuyerLeadDnDColumnFragment) => (
      <BuyerLeadKanbanItem
        lead={data}
        onDelete={leadId => setLeadToDelete(leadId)}
        includeLotReference={includeLotReference}
        readOnly={editableById.get(data.id) === false}
      />
    ),
    [includeLotReference, editableById],
  );

  const canDragItem = useCallback(
    (item: BuyerLeadDnDColumnFragment) => editableById.get(item.id) === true,
    [editableById],
  );

  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const getItemBorderColor = useCallback(
    (item: BuyerLeadDnDColumnFragment) =>
      getStageCardColors(item.lead_stage?.status, palette).border,
    [palette],
  );

  const onItemClickedGated = useMemo(
    () =>
      onItemClicked == null
        ? undefined
        : (item: BuyerLeadDnDColumnFragment) => {
            if (editableById.get(item.id) === true) {
              onItemClicked(item);
            }
          },
    [onItemClicked, editableById],
  );

  return (
    <>
      <BuyerLeadDeleteDialog
        buyerLeadId={leadToDelete}
        open={leadToDelete != null}
        onClose={() => setLeadToDelete(null)}
        onDelete={() => {
          setLeadToDelete(null);
        }}
      />
      {useMemo(
        () => (
          <KanbanColumn
            colId={columnId}
            getItemId={item => item.id}
            items={items}
            index={index}
            renderItem={renderItem}
            title={columnTitle}
            width={270}
            onItemClicked={onItemClickedGated}
            endControls={endControls}
            isDroppable={!readOnly}
            canDragItem={canDragItem}
            getItemBorderColor={getItemBorderColor}
          />
        ),
        [
          columnId,
          items,
          columnTitle,
          index,
          onItemClickedGated,
          endControls,
          renderItem,
          readOnly,
          canDragItem,
          getItemBorderColor,
        ],
      )}
    </>
  );
};
